import { useState, useEffect, useRef } from 'react';
import { Search, MapPin, Star, Clock, ExternalLink, Loader2, Navigation } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API } from '@/config/api';

/**
 * Google Places Enhanced Search Component
 * Searches all restaurants via Google Places API
 * Highlights Chain29 partner restaurants
 */
export default function GooglePlacesSearch({ onSearchResults, className }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const debounceRef = useRef(null);

  // Get user's location
  const getUserLocation = () => {
    setLocationLoading(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setLocationLoading(false);
        },
        (error) => {
          console.error('Location error:', error);
          setLocationLoading(false);
        },
        { timeout: 10000 }
      );
    } else {
      setLocationLoading(false);
    }
  };

  // Fetch autocomplete suggestions
  useEffect(() => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ input: query });
        if (userLocation) {
          params.append('lat', userLocation.lat);
          params.append('lng', userLocation.lng);
        }
        
        const response = await axios.get(`${API}/places/autocomplete?${params}`);
        setSuggestions(response.data.predictions || []);
      } catch (error) {
        console.error('Autocomplete error:', error);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, userLocation]);

  // Search restaurants
  const handleSearch = async (searchQuery = query) => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    setOpen(false);
    
    try {
      const params = new URLSearchParams({ query: searchQuery });
      if (userLocation) {
        params.append('lat', userLocation.lat);
        params.append('lng', userLocation.lng);
      }
      
      const response = await axios.get(`${API}/places/search?${params}`);
      setSearchResults(response.data.results || []);
      
      if (onSearchResults) {
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Handle suggestion select
  const handleSelectSuggestion = (suggestion) => {
    setQuery(suggestion.main_text || suggestion.description);
    handleSearch(suggestion.main_text || suggestion.description);
  };

  // Navigate to restaurant
  const handleRestaurantClick = (restaurant) => {
    if (restaurant.is_partner && restaurant.partner_id) {
      navigate(`/restaurant/${restaurant.partner_id}`);
    } else {
      // For non-partners, show details or "not available" message
      // Could also open a modal with Google Places details
      window.open(`https://www.google.com/maps/place/?q=place_id:${restaurant.place_id}`, '_blank');
    }
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Search Input with Autocomplete */}
      <div className="flex gap-2 p-2 bg-card rounded-2xl shadow-lg border">
        <Popover open={open && suggestions.length > 0} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search any restaurant..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setOpen(true);
                }}
                onFocus={() => query.length >= 2 && setOpen(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                className="pl-10 border-0 bg-transparent focus-visible:ring-0"
                data-testid="places-search-input"
              />
            </div>
          </PopoverTrigger>
          
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandList>
                {loading ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : suggestions.length === 0 ? (
                  <CommandEmpty>No restaurants found</CommandEmpty>
                ) : (
                  <CommandGroup heading="Suggestions">
                    {suggestions.map((suggestion) => (
                      <CommandItem
                        key={suggestion.place_id}
                        onSelect={() => handleSelectSuggestion(suggestion)}
                        className="cursor-pointer"
                      >
                        <MapPin className="h-4 w-4 mr-2 text-muted-foreground" />
                        <div className="flex flex-col">
                          <span className="font-medium">{suggestion.main_text}</span>
                          <span className="text-xs text-muted-foreground">
                            {suggestion.secondary_text}
                          </span>
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>

        {/* Location Button */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={getUserLocation}
          disabled={locationLoading}
          title="Use my location"
        >
          {locationLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Navigation className={cn("h-4 w-4", userLocation && "text-primary")} />
          )}
        </Button>

        {/* Search Button */}
        <Button 
          onClick={() => handleSearch()} 
          disabled={searching}
          className="btn-pill px-8"
          data-testid="places-search-button"
        >
          {searching ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Search
        </Button>
      </div>

      {/* Location indicator */}
      {userLocation && (
        <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
          <MapPin className="h-3 w-3" />
          <span>Searching near your location</span>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Search Results</h2>
            <Badge variant="outline">
              {searchResults.filter(r => r.is_partner).length} Chain29 Partners
            </Badge>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {searchResults.map((restaurant) => (
              <RestaurantCard 
                key={restaurant.place_id} 
                restaurant={restaurant}
                onClick={() => handleRestaurantClick(restaurant)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Restaurant Card Component
 * Shows Google Places result with partner highlighting
 */
function RestaurantCard({ restaurant, onClick }) {
  const priceLevel = restaurant.price_level 
    ? '$'.repeat(restaurant.price_level) 
    : null;

  return (
    <Card 
      className={cn(
        "overflow-hidden cursor-pointer transition-all hover:shadow-lg",
        restaurant.is_partner && "ring-2 ring-primary/50"
      )}
      onClick={onClick}
      data-testid={`restaurant-card-${restaurant.place_id}`}
    >
      {/* Image */}
      <div className="relative h-40 bg-muted">
        {restaurant.photo_url ? (
          <img 
            src={restaurant.photo_url} 
            alt={restaurant.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              // No stock photo: hide the broken image and leave the muted tile
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted to-muted-foreground/20">
            <MapPin className="h-12 w-12 text-muted-foreground/50" />
          </div>
        )}
        
        {/* Partner Badge */}
        {restaurant.is_partner ? (
          <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground">
            Chain29 Partner
          </Badge>
        ) : (
          <Badge variant="secondary" className="absolute top-2 left-2">
            <ExternalLink className="h-3 w-3 mr-1" />
            View on Google
          </Badge>
        )}

        {/* Open/Closed Badge */}
        {restaurant.is_open !== null && (
          <Badge 
            variant={restaurant.is_open ? "default" : "destructive"}
            className="absolute top-2 right-2"
          >
            {restaurant.is_open ? "Open" : "Closed"}
          </Badge>
        )}
      </div>

      <CardContent className="p-4">
        <h3 className="font-semibold text-lg truncate">{restaurant.name}</h3>
        
        <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
          {restaurant.rating && (
            <span className="flex items-center gap-1">
              <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              {restaurant.rating.toFixed(1)}
              {restaurant.user_ratings_total && (
                <span className="text-xs">({restaurant.user_ratings_total})</span>
              )}
            </span>
          )}
          {priceLevel && <span>{priceLevel}</span>}
        </div>

        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
          <MapPin className="h-3 w-3 inline mr-1" />
          {restaurant.address}
        </p>

        {/* Action Button */}
        <div className="mt-4">
          {restaurant.is_partner ? (
            <Button className="w-full" size="sm">
              Order Now
            </Button>
          ) : (
            <Button variant="outline" className="w-full" size="sm">
              <ExternalLink className="h-4 w-4 mr-2" />
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Nearby Restaurants Component
 * Shows restaurants near user's location
 */
export function NearbyRestaurants({ className }) {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchNearby = async () => {
    setLoading(true);
    setError(null);
    
    if (!navigator.geolocation) {
      setError("Geolocation not supported");
      setLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const params = new URLSearchParams({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            radius: 5000
          });
          
          const response = await axios.get(`${API}/places/nearby?${params}`);
          setRestaurants(response.data.results || []);
        } catch (err) {
          setError("Failed to load nearby restaurants");
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError("Location access denied");
        setLoading(false);
      }
    );
  };

  return (
    <div className={className}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <MapPin className="h-5 w-5" />
          Nearby Restaurants
        </h2>
        <Button variant="outline" size="sm" onClick={fetchNearby} disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Find Nearby"}
        </Button>
      </div>

      {error && (
        <p className="text-sm text-muted-foreground">{error}</p>
      )}

      {restaurants.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {restaurants.slice(0, 8).map((restaurant) => (
            <RestaurantCard
              key={restaurant.place_id}
              restaurant={restaurant}
              onClick={() => {
                if (restaurant.is_partner && restaurant.partner_id) {
                  navigate(`/restaurant/${restaurant.partner_id}`);
                } else {
                  window.open(`https://www.google.com/maps/place/?q=place_id:${restaurant.place_id}`, '_blank');
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
